import React, { useMemo, useState } from 'react';
import { IAbstractRecord } from '@models';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError } from '@utils/graph-utils';
import { getPolicyPlanCoverInputs } from './content';
import { useMutation } from '@apollo/client';
import { overridePolicyPlanCover } from './queries';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';

interface IPolicyPlanCoverDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  data: IAbstractRecord;
  onSuccess?: () => void;
}

const PolicyPlanCoverDetailsDrawer: React.FC<
  IPolicyPlanCoverDetailsDrawerProps
> = ({ open, onClose, onSuccess, data }) => {
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const inputForm = useMemo(() => getPolicyPlanCoverInputs(data), [data]);

  const [overridePolicyPlanCoverMutation] = useMutation(
    overridePolicyPlanCover()
  );

  const handleSubmit = async (formValues: Record<string, any>) => {
    setFormDisabled(true);
    setSubmitButtonState('loading');
    try {
      const [formData] = normalizeDynamicFormDrawerValues(
        inputForm,
        formValues
      );

      const amendmentCoverPremiumType = data?.AmendmentCoverPremiumType;
      const amendmentExcessOnClaimType = data?.AmendmentExcessOnClaimType;

      const SumInsuredVariance = parseFloat(formData.SumInsuredVariance) || 0;

      let PremiumValueVariance: number | undefined = undefined;
      let PremiumPercentageVariance: number | undefined = undefined;

      if (amendmentCoverPremiumType === 'VALUE') {
        PremiumValueVariance = parseFloat(formData.PremiumValueVariance) || 0;
      } else if (amendmentCoverPremiumType === 'PERCENTAGE') {
        PremiumPercentageVariance =
          (parseFloat(formData.PremiumPercentageVariance) || 0) / 100;
      }

      let ExcessPercentageVariance: number | undefined = undefined;
      let ExcessValueVariance: number | undefined = undefined;
      let ExcessDaysVariance: number | undefined = undefined;

      if (amendmentExcessOnClaimType === 'PERCENTAGE') {
        ExcessPercentageVariance =
          (parseFloat(formData.ExcessPercentageVariance) || 0) / 100;
      } else if (amendmentExcessOnClaimType === 'VALUE') {
        ExcessValueVariance = parseFloat(formData.ExcessValueVariance) || 0;
      } else if (amendmentExcessOnClaimType === 'DAYS') {
        ExcessDaysVariance = parseFloat(formData.ExcessDaysVariance) || 0;
      }

      const policyPlanCoverVarianceId = data?.id;
      if (!policyPlanCoverVarianceId) {
        throw new Error(
          'No policyPlanCoverVarianceId (data.id) found in row data.'
        );
      }

      await overridePolicyPlanCoverMutation({
        variables: {
          policyPlanCoverVarianceId,
          SumInsuredVariance,
          PremiumValueVariance,
          PremiumPercentageVariance,
          ExcessPercentageVariance,
          ExcessValueVariance,
          ExcessDaysVariance,
        },
      });

      toast.success(
        <ToastSuccessMessage>
          Policy Plan Cover updated successfully!
        </ToastSuccessMessage>
      );
      setSubmitButtonState('success');
      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage = getError({ err });
      toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      setSubmitButtonState(undefined);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      isOpen={open}
      title="Override Policy Plan Cover"
      sections={inputForm}
      onSubmit={handleSubmit}
      buttonText="Submit"
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      onClose={onClose}
    />
  );
};

export default PolicyPlanCoverDetailsDrawer;
